import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useHasScope } from "../../../../../config/globalConfig";
import { customTheme } from "../../../../../config/theme/MuiTheme";
import SectionHeader from "../../../../../util/component/SectionHeader";
import { ContextOperation } from "../../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../../hooks/useAgGridCustomDatasource";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useToggle from "../../../hooks/useToggle";
import { Parser } from "../../../utils";
import AgGridOperationTable from "../../common/AgGridOperationTable";
import EmptyStateGrid from "../../common/EmptyStateGrid";
import EnumChangeName from "../../common/EnumChangeName";
import { nameof } from "../../common/ObjectUtils";
import TransferReasonService from "../TransferReasonService";
import { TransferReasonTypes } from "../Types";
import Dialogs from "./dialogs";

const CarriersList = () => {
  const hasScope = useHasScope;
  const { tenant } = useContext(ContextOperation);
  const { locationType } = EnumChangeName();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tenantCallout = queryParams.get('tenant');

  const { id } = useParams();

  const t = useOperationTranslation();

  const gridRef = useRef<AgGridReact<TransferReasonTypes.ICarrierTender>>(null);

  const navigate = useNavigate();

  const [approveConfirm, toggleApproveConfirm] = useToggle();
  const [messageConfirm, toggleMessageConfirm] = useToggle();

  const [transferRequestDetails, setTransferRequestDetails] = useState<
    TransferReasonTypes.TransferRequestDetails | undefined
  >();
  const [selectedCarrier, setSelectedCarrier] = useState<
    TransferReasonTypes.ICarrierTender | undefined
  >();
  const [chosenTender, setChosenTender] = useState<
    TransferReasonTypes.ICarrierTender | undefined
  >();

  useEffect(() => {
    TransferReasonService.get
    .transferRequestDetails(!!tenantCallout ? tenantCallout : tenant, id)
      .then((response) => {
        setTransferRequestDetails(response);
      });
  }, []);

  const onToggleConfirmation = (
    func: () => void,
    carrierTender?: TransferReasonTypes.ICarrierTender
  ) => {
    setSelectedCarrier(carrierTender);
    func();
  };

  const onMessageSent = () => {
    gridRef.current?.api.forEachNode((node) => {
      if (
        node.data?.carrierName === selectedCarrier?.carrierName &&
        node.data
      ) {
        node.updateData({
          ...node.data,
          isSend: true,
        });
      }
    });
  };

  const onApproveTender = () => {
    gridRef.current?.api.forEachNode((node) => {
      if (node.data) {
        node.updateData({
          ...node.data,
          isSend: true,
          isApproved: true,
        });
      }
    });

    setChosenTender(selectedCarrier);
  };

  const [datasource, totalElements] = useAgGridCustomDatasource(
    TransferReasonService.get.carriersTender,
    [!!tenantCallout ? tenantCallout : tenant, transferRequestDetails?.number],
    [transferRequestDetails?.number]
  );

  const fieldFunction = () => {
    if (hasScope("SCOPE_UPDATE_MDTRANSPORTATION_TRANSFER_REQUEST"))
      return "_actions";
    else return "";
  };

  const columns: ColDef[] = [
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.type"),
      field: nameof<TransferReasonTypes.ICarrierTender>("type"),
      flex: 1,
    },
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.startValidity"),
      field: nameof<TransferReasonTypes.ICarrierTender>("startValidity"),
      flex: 1,
    },
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.endValidity"),
      field: nameof<TransferReasonTypes.ICarrierTender>("endValidity"),
      flex: 1,
    },
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.loadIndex"),
      field: nameof<TransferReasonTypes.ICarrierTender>("loadIndex"),
      flex: 1,
    },
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.companyName"),
      field: nameof<TransferReasonTypes.ICarrierTender>("carrierName"),
      tooltipField: nameof<TransferReasonTypes.ICarrierTender>("carrierName"),
      flex: 1,
    },
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.city"),
      field: nameof<TransferReasonTypes.ICarrierTender>("carrierCity"),
      tooltipField: nameof<TransferReasonTypes.ICarrierTender>("carrierCity"),
      flex: 1,
    },
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.address"),
      field: nameof<TransferReasonTypes.ICarrierTender>("carrierAddress"),
      tooltipField:
        nameof<TransferReasonTypes.ICarrierTender>("carrierAddress"),
      flex: 1,
    },
    {
      headerName: t("transferReason.manageCarriers.tableHeaders.price"),
      field: nameof<TransferReasonTypes.ICarrierTender>("finalPrice"),
      flex: 1,
      cellRenderer: (
        params: ICellRendererParams<TransferReasonTypes.ICarrierTender>
      ) => {
        return `€ ${Parser.price(params.value)}`;
      },
    },
    {
      headerName: "",
      field: fieldFunction(),
      minWidth: 100,
      maxWidth: 100,
      cellRenderer: (
        params: ICellRendererParams<TransferReasonTypes.ICarrierTender>
      ) => {
        return (
          <Mui.Grid container spacing={1}>
            {hasScope("SCOPE_UPDATE_MDTRANSPORTATION_TRANSFER_REQUEST") && (
              <>
                <Mui.Grid item xs="auto">
                  <Mui.Tooltip
                    title={t(
                      "transferReason.manageCarriers.tableHeaders.tooltips.sendInvitation"
                    )}
                  >
                    <Mui.Button
                      variant="text"
                      size="icon"
                      onClick={() =>
                        onToggleConfirmation(toggleMessageConfirm, params.data)
                      }
                      disabled={params.data?.isSend}
                    >
                      <MuiIcons.Send />
                    </Mui.Button>
                  </Mui.Tooltip>
                </Mui.Grid>

                <Mui.Grid item xs="auto">
                  <Mui.Tooltip
                    title={t(
                      "transferReason.manageCarriers.tableHeaders.tooltips.assign"
                    )}
                  >
                    <Mui.Button
                      variant="text"
                      size="icon"
                      onClick={() =>
                        onToggleConfirmation(toggleApproveConfirm, params.data)
                      }
                      disabled={!params.data?.isSend || params.data?.isApproved}
                    >
                      <MuiIcons.CheckCircleRounded />
                    </Mui.Button>
                  </Mui.Tooltip>
                </Mui.Grid>
              </>
            )}
          </Mui.Grid>
        );
      },
    },
  ];

  return (
    <>
      <SectionHeader
        title={
          t("transferReason.manageCarriers.pageTitleFirst") +
          " " +
          t("transferReason.manageCarriers.pageTitleSecond")
        }
        subtitle={t("transferReason.manageCarriers.subtitle")}
      />

      <Mui.Stack
        padding={2}
        spacing={2}
        border={1}
        borderRadius={1}
        borderColor={customTheme.palette.neutral.dark}
        margin={2}
      >
        <Mui.Grid container spacing={2}>
          <Mui.Grid item>
            <Mui.Button
              variant="text"
              size="icon"
              onClick={() => navigate("/operation/transfer/transferRequests")}
            >
              <MuiIcons.ArrowBack />
            </Mui.Button>
          </Mui.Grid>
          <Mui.Grid item xs={10}>
            <Mui.Typography variant="h6">
              {t("transferReason.manageCarriers.backButton")}
            </Mui.Typography>
          </Mui.Grid>

          <Mui.Grid item xs={12}>
            <Mui.Typography variant="h6">
              {t("transferReason.manageCarriers.carriersTender")}
            </Mui.Typography>
          </Mui.Grid>

          <Mui.Grid item container xs={4} spacing={1} alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("transferReason.manageCarriers.startStation")}
              </Mui.Typography>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Typography variant="body">
                {
                  transferRequestDetails?.startStationId ? 
                    `${transferRequestDetails.startStationDescription} (${locationType(transferRequestDetails.startStationType)})` :
                    ""
                }
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid item container xs={4} spacing={1} alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("transferReason.manageCarriers.predictedArrivalStation")}
              </Mui.Typography>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Typography variant="body">
                {
                  transferRequestDetails?.expectedEndStationId ?
                    `${transferRequestDetails.expectedEndStationDescription} (${locationType(transferRequestDetails.expectedEndStationType)})` :
                    ""
                }
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid item container xs={4} spacing={1} alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("transferReason.manageCarriers.distance")}
              </Mui.Typography>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Typography variant="body">
                {`${transferRequestDetails?.routeKm} KM`}
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid item container xs={4} spacing={1} alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("transferReason.manageCarriers.startDate")}
              </Mui.Typography>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Typography variant="body">
                {dayjs(transferRequestDetails?.startDate).format(
                  "DD/MM/YYYY HH:mm"
                )}
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid item container xs={4} spacing={1} alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("transferReason.manageCarriers.predictedArrivalDate")}
              </Mui.Typography>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Typography variant="body">
                {dayjs(transferRequestDetails?.expectedEndDate).format(
                  "DD/MM/YYYY HH:mm"
                )}
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid item container xs={4} spacing={1} alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("transferReason.manageCarriers.loadIndex")}
              </Mui.Typography>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Typography variant="body">
                {transferRequestDetails?.loadIndex}
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>

        <Mui.Box
          border={1}
          borderRadius={1}
          borderColor={customTheme.palette.neutral.dark}
          padding={2}
        >
          <Mui.Grid item xs={12}>
            <Mui.Typography>
              {t("transferReason.manageCarriers.chosenTender")}
            </Mui.Typography>
          </Mui.Grid>

          <Mui.Grid container spacing={1}>
            <Mui.Grid item container xs={2} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.type")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.type}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.startValidity")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.startValidity}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.endValidity")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.endValidity}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.loadIndexSecond")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.loadIndex}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.companyName")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.carrierName}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.city")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.carrierCity}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.address")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.carrierAddress}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.price")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender?.finalPrice &&
                    `€ ${Parser.price(chosenTender?.finalPrice)}`}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.transferRequestNumber")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender && transferRequestDetails?.number}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={2.5} spacing={1} alignItems="center">
              <Mui.Grid item xs="auto">
                <Mui.Typography variant="h6">
                  {t("transferReason.manageCarriers.ApprovationDate")}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Typography variant="body">
                  {chosenTender && dayjs(new Date()).format("YYYY-MM-DD HH:mm")}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Box>

        <Mui.Card>
          <AgGridOperationTable
            gridRef={gridRef}
            serverSideDatasource={datasource}
            columnDefs={columns}
            tooltipShowDelay={0}
            totalElements={totalElements}
            emptyState={{
              title: t("transferReason.manageCarriers.tableHeaders.emptyTitle"),
              content: t("transferReason.manageCarriers.tableHeaders.emptyContent")
            }}
          />

        </Mui.Card>
      </Mui.Stack>

      <Dialogs.SendMessage
        open={messageConfirm}
        onClose={toggleMessageConfirm}
        onMessageSent={onMessageSent}
        requestId={transferRequestDetails?.id}
        carrier={selectedCarrier}
        transferRequestNumber={transferRequestDetails?.number}
      />

      <Dialogs.ApproveConfirmation
        open={approveConfirm}
        onClose={toggleApproveConfirm}
        onApproveTender={onApproveTender}
        carrier={selectedCarrier}
        requestId={transferRequestDetails?.id}
        tenant={transferRequestDetails?.tenant}
      />
    </>
  );
};
export default CarriersList;
